// =====================================================
// STORY TEXT: reading the text of footnotes, endnotes, headers and footers (the parts the body
// comparison does not see), and finding their paragraphs.
// =====================================================

// (invisible characters that change nothing on the page, zero-width spaces and the like, do
// not count, as in the body's comparison: a header whose only difference was some made it
// "differ but could not be marked")
export const NOTE_TEXT_NORM = (s: string) => s.replace(/[\u200B-\u200F\uFEFF\u2060\u00AD\u034F\u061C]/g, '').replace(/\s+/g, ' ').trim();

/** The text of one WordprocessingML fragment: w:t/w:delText concatenated exactly,
 *  tabs and breaks as spaces (mirrors how Word reads it). */
export function fragmentText(xml: string): string {
  let t = xml.replace(/<w:(?:tab|br)\b[^>]*\/>/g, '<w:t> </w:t>').replace(/<\/w:p>/g, '<w:t> </w:t>');
  let out = '';
  for (const m of t.matchAll(/<w:(?:t|delText)\b[^>]*>([\s\S]*?)<\/w:(?:t|delText)>/g)) out += m[1];
  return out
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'");
}

/**
 * A header or footer with the shown value of its page-number fields (PAGE, NUMPAGES,
 * SECTIONPAGES) removed. Word stores the number it last displayed, which depends on
 * where the part was last shown ("Page 1" in one draft, "Page 8" in the other): not a change
 * to the text, so it must not be compared as one.
 */
export const PAGE_FIELD = /^\s*(?:PAGE|NUMPAGES|SECTIONPAGES)\b/i;
export function withoutPageNumbers(xml: string): string {
  // simple fields: <w:fldSimple w:instr=" PAGE "><w:r><w:t>8</w:t></w:r></w:fldSimple>
  let out = xml.replace(/<w:fldSimple\b([^>]*?)(\/?)>([\s\S]*?<\/w:fldSimple>)?/g, (m, attrs: string, selfClose: string) => {
    const instr = (attrs.match(/\bw:instr="([^"]*)"/) || [])[1] || '';
    if (selfClose || !PAGE_FIELD.test(instr)) return m;
    return '';
  });
  // complex fields: begin, instrText, separate, the shown runs, end (nesting kept on a stack)
  const stack: { instr: string; result: boolean }[] = [];
  out = out.replace(/<w:fldChar\b[^>]*?w:fldCharType="(begin|separate|end)"[^>]*>|<w:instrText\b[^>]*>([^<]*)<\/w:instrText>|<w:t\b([^>]*)>([^<]*)<\/w:t>/g,
    (m, charType: string | undefined, instr: string | undefined, tAttrs: string | undefined) => {
      if (charType === 'begin') stack.push({ instr: '', result: false });
      else if (charType === 'separate') { if (stack.length) stack[stack.length - 1].result = true; }
      else if (charType === 'end') stack.pop();
      else if (instr !== undefined) { if (stack.length) stack[stack.length - 1].instr += instr; }
      else if (tAttrs !== undefined) {
        const top = stack[stack.length - 1];
        if (top && top.result && PAGE_FIELD.test(top.instr)) return `<w:t${tAttrs}></w:t>`;
      }
      return m;
    });
  return out;
}

/** Top-level <w:p> fragments of a note definition. */
export function noteParas(defXml: string): string[] {
  const out: string[] = [];
  const re = /<(\/?)w:p\b[^>]*?(\/?)>/g;
  let depth = 0, start = -1, m: RegExpExecArray | null;
  while ((m = re.exec(defXml)) !== null) {
    if (m[2]) continue; // self-closing <w:p/>
    if (!m[1]) { if (depth === 0) start = m.index; depth++; }
    else { depth--; if (depth === 0 && start >= 0) { out.push(defXml.slice(start, re.lastIndex)); start = -1; } }
  }
  return out;
}

/** Where each paragraph of a part is (as noteParas finds them: a paragraph inside a table cell
 *  counts, one inside another paragraph's text box is part of that paragraph). */
export function paraSpans(x: string): { start: number; end: number }[] {
  const out: { start: number; end: number }[] = [];
  const re = /<(\/?)w:p\b[^>]*?(\/?)>/g;
  let depth = 0, start = -1, m: RegExpExecArray | null;
  while ((m = re.exec(x)) !== null) {
    if (m[2]) continue;
    if (!m[1]) { if (depth === 0) start = m.index; depth++; }
    else { depth--; if (depth === 0 && start >= 0) { out.push({ start, end: re.lastIndex }); start = -1; } }
  }
  return out;
}

/** A paragraph's properties, whole: a tracked change to them (w:pPrChange) holds its own
 *  <w:pPr>…</w:pPr>, so a plain non-greedy match would stop inside it and cut the XML. */
export const PPR_RE = /<w:pPr\b[^>]*\/>|<w:pPr\b[^>]*>(?:<w:pPrChange\b[\s\S]*?<\/w:pPrChange>|(?!<\/w:pPr>)[\s\S])*?<\/w:pPr>/;

/** Per-paragraph structural guard: paragraphs whose content a text-level rebuild
 *  would destroy (tables, drawings, fields, content controls). */
export const STRUCTURAL_PARA = /<w:(?:tbl|txbxContent|drawing|pict|object|fldChar|fldSimple|instrText|sdt)\b/;
